import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
    QueueExtractionEvent,
    UploadQueueFailureEvent,
    UploadQueueRow,
    UseSequentialFileUploadQueueOptions,
    UseSequentialFileUploadQueueReturn
} from "./types";

const DEFAULT_ERROR_MESSAGE = "Upload failed";
const UPLOAD_WEIGHT = 30;
const EXTRACT_WEIGHT = 70;

function stageForExtractionEvent(status: string): "extracting" | "indexed" | null {
    switch (status) {
        case "started":
        case "downloading":
        case "converting":
        case "saving_text":
        case "chunking":
        case "chunked":
        case "saving_metadata":
        case "processing_chunks":
        case "cleaning":
        case "embedding_chunk":
        case "committing":
            return "extracting";
        case "completed":
            return "indexed";
        default:
            return null;
    }
}

function friendlyError(raw: string | null): string {
    if (raw && /\b401\b/.test(raw)) return "Session expired - please retry.";
    return raw ?? DEFAULT_ERROR_MESSAGE;
}

export function useSequentialFileUploadQueue({
    handleUpload,
    onFileSucceeded,
    onFileFailed,
    registerLifecycleHandlers
}: UseSequentialFileUploadQueueOptions): UseSequentialFileUploadQueueReturn {
    const [rows, setRows] = useState<UploadQueueRow[]>([]);
    const [isRunning, setIsRunning] = useState(false);
    const filesRef = useRef(new Map<string, File>());
    const seqRef = useRef(0);
    const activeRowIdRef = useRef<string | null>(null);
    const lastErrorRef = useRef<string | null>(null);

    const patchRow = useCallback((rowId: string, patch: Partial<UploadQueueRow>) => {
        setRows(prev => prev.map(row => (row.id === rowId ? { ...row, ...patch } : row)));
    }, []);

    const enqueueFiles = useCallback((files: File[]) => {
        if (files.length === 0) return;
        const nextRows: UploadQueueRow[] = files.map(file => {
            const id = `row-${seqRef.current++}`;
            filesRef.current.set(id, file);
            return {
                id,
                name: file.name,
                stage: "queued",
                progress: 0,
                fileId: null,
                error: null
            };
        });
        setRows(prev => [...prev, ...nextRows]);
    }, []);

    const removeQueuedRow = useCallback((rowId: string) => {
        setRows(prev => {
            const target = prev.find(row => row.id === rowId);
            if (!target || target.stage !== "queued") return prev;
            filesRef.current.delete(rowId);
            return prev.filter(row => row.id !== rowId);
        });
    }, []);

    const retryRow = useCallback((rowId: string) => {
        setRows(prev => {
            const target = prev.find(row => row.id === rowId);
            if (!target || target.stage !== "error" || !filesRef.current.has(rowId)) return prev;
            return prev.map(row =>
                row.id === rowId
                    ? { ...row, stage: "queued", progress: 0, fileId: null, error: null }
                    : row
            );
        });
    }, []);

    const lifecycleHandlers = useMemo(() => ({
        onUploadProgress: (progress: number) => {
            const rowId = activeRowIdRef.current;
            if (!rowId) return;
            patchRow(rowId, {
                stage: "uploading",
                progress: Math.round((progress * UPLOAD_WEIGHT) / 100)
            });
        },
        onSavingFileToDatabaseStart: () => {
            const rowId = activeRowIdRef.current;
            if (!rowId) return;
            patchRow(rowId, {
                stage: "extracting",
                progress: UPLOAD_WEIGHT
            });
        },
        onFileTextExtractionEvent: (event: QueueExtractionEvent) => {
            const rowId = activeRowIdRef.current;
            if (!rowId) return;
            const stage = stageForExtractionEvent(event.status);
            if (!stage || stage === "indexed") return;
            const eventProgress = event.progress ?? 0;
            patchRow(rowId, {
                stage,
                progress: UPLOAD_WEIGHT + Math.round((eventProgress * EXTRACT_WEIGHT) / 100)
            });
        },
        onUploadError: (message: string) => {
            lastErrorRef.current = message;
        }
    }), [patchRow]);

    useEffect(() => {
        if (!registerLifecycleHandlers) return;
        registerLifecycleHandlers(lifecycleHandlers);
    }, [registerLifecycleHandlers, lifecycleHandlers]);

    useEffect(() => {
        if (activeRowIdRef.current) return;
        const next = rows.find(row => row.stage === "queued");
        if (!next) return;
        const file = filesRef.current.get(next.id);
        if (!file) return;

        activeRowIdRef.current = next.id;
        lastErrorRef.current = null;
        setIsRunning(true);
        patchRow(next.id, { stage: "uploading", progress: 0, error: null });

        void (async () => {
            let failure: UploadQueueFailureEvent | null = null;
            try {
                const fileId = await handleUpload(file);
                if (fileId != null) {
                    patchRow(next.id, { stage: "indexed", progress: 100, fileId, error: null });
                    filesRef.current.delete(next.id);
                    onFileSucceeded?.({ rowId: next.id, fileId, file });
                } else {
                    failure = { rowId: next.id, file, error: friendlyError(lastErrorRef.current) };
                    patchRow(next.id, { stage: "error", progress: 100, error: failure.error });
                }
            } catch (error) {
                const message = error instanceof Error && error.message ? error.message : DEFAULT_ERROR_MESSAGE;
                failure = { rowId: next.id, file, error: friendlyError(message) };
                patchRow(next.id, { stage: "error", progress: 100, error: failure.error });
            } finally {
                activeRowIdRef.current = null;
                setIsRunning(false);
                if (failure) onFileFailed?.(failure);
            }
        })();
    }, [rows, handleUpload, onFileSucceeded, onFileFailed, patchRow]);

    return { rows, enqueueFiles, removeQueuedRow, retryRow, isRunning };
}
